<template>
    <div class="space-y-1" :data-refresh-tick="refreshTick">
        <!-- Overall — the convenience property. Editable only while the four
             effective sides are equal; a mixed set disables it (and shows
             "mixed" instead of a number) because there is no single value it
             could display without silently discarding the side overrides. -->
        <!-- Same three-column row as every other property (see PropertyRow.vue).
             Overall's status tracks the `padding` shorthand itself (reset drops
             it back to the theme/schema value); "mixed" is conveyed by the
             input's own placeholder, not by a badge. -->
        <property-row :label="overallLabel"
            :resettable="true"
            :modified="overallModified"
            :inherited="state !== 'default'"
            @reset="resetOverall">
            <div class="flex min-w-0 items-center">
                <input type="number" :min="inputMin('padding')" :max="inputMax('padding')" class="w-full min-w-0 flex-1 px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none disabled:bg-slate-500/40 disabled:text-slate-300 disabled:border-slate-500"
                    :value="overallValue ?? ''"
                    :disabled="!overallEnabled"
                    :placeholder="overallEnabled ? '' : 'mixed'"
                    :title="overallEnabled ? 'Sets padding for all four sides' : 'Sides differ — set them equal (or type one below) to use Overall again'"
                    @change="onOverall($event.target.value)" />
                <button v-if="collapsible" type="button"
                    class="ml-1 shrink-0 px-1 text-gray-500 hover:text-gray-900 focus:outline-none"
                    :aria-expanded="expanded"
                    :aria-label="expanded ? 'Hide individual padding sides' : 'Show individual padding sides'"
                    :title="expanded ? 'Hide individual padding sides' : 'Show individual padding sides'"
                    @click.stop="toggleExpanded">{{ expanded ? '▲' : '▼' }}</button>
            </div>
        </property-row>

        <!-- Per-side overrides, in Top / Right / Bottom / Left order. Each field
             shows the EFFECTIVE value (override, else the overall padding, else
             0) so what is displayed is always what is rendered. -->
        <template v-if="!collapsible || expanded">
        <property-row v-for="side in sides" :key="side.prop" :label="side.label"
            :resettable="true"
            :modified="isExplicit(side.prop)"
            :inherited="true"
            @reset="resetSide(side.prop)">
            <input type="number" :min="inputMin(side.prop)" :max="inputMax(side.prop)" class="w-full min-w-0 px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                :value="effective[side.key]"
                @change="onSide(side.prop, $event.target.value)" />
        </property-row>
        </template>
    </div>
</template>

<script>
// Padding editor: Overall + Top/Right/Bottom/Left for one part.
//
// It owns no state and no fallback logic of its own — every displayed value
// comes from resolvePadding(node.style(part, state)), i.e. the full cascade
// (schema defaults, theme, local overrides, state overrides), and every write
// goes through the two helpers in v2/padding.js that also own the
// normalization rule (all four sides equal -> back to the `padding`
// shorthand). Because those helpers write several properties at once, each edit
// is wrapped in runPropertyTransaction: one PropertyChangeCommand per edit,
// with the debounced path suppressed for the duration so an unrelated edit made
// moments later can never be absorbed into the same undo entry.
import {
    paddingEditorState,
    applyOverallPadding,
    applySidePadding,
    resetOverallPadding,
    PADDING_SIDE_PROPS,
    PADDING_SIDE_KEYS,
} from '../../Hasp/properties/v2/padding.js';
import { runPropertyTransaction } from '../../history/propertyTransaction.js';
import { getObjectPage } from '../../utils/hasputils';
import PropertyRow from './PropertyRow.vue';

export default {
    name: 'PaddingEditor',
    components: { PropertyRow },

    props: {
        node:     { type: Object, required: true },
        partName: { type: String, default: 'main' },
        state:    { type: String, default: 'default' },
        overallLabel: { type: String, default: 'overall' },
        // Legacy slider knob.padding is a total extra size, whereas direct
        // pad_* values are raw LVGL sides. Gauge keeps the default scale 1.
        overallScale: { type: Number, default: 1 },
        // Slider Knob's new Overall control writes four raw sides, leaving
        // `padding` only as a read/import compatibility fallback.
        allAsSides: { type: Boolean, default: false },
        // Gauge remains expanded as before. Slider Knob uses the same editor
        // in compact compound-property mode.
        collapsible: { type: Boolean, default: false },
    },

    data() {
        return { refreshTick: 0, expanded: false };
    },

    // Same pattern as PartPropertiesPanel: the node is deliberately non-reactive,
    // so re-render on its own style-refresh events.
    mounted() {
        this._subs = ['padding', ...PADDING_SIDE_PROPS]
            .filter(prop => this.node.hasStyleProperty(this.partName, prop))
            .map(prop => {
                const handler = () => { this.refreshTick++; };
                this.node.onStyle(this.partName, prop, handler);
                return { prop, handler };
            });
    },

    beforeUnmount() {
        for (const { prop, handler } of this._subs ?? []) {
            this.node.offStyle(this.partName, prop, handler);
        }
    },

    computed: {
        // Only parts that actually declare the overrides get the side fields;
        // a part with just `padding` shows Overall alone.
        sides() {
            return PADDING_SIDE_PROPS
                .filter(prop => this.node.hasStyleProperty(this.partName, prop))
                .map(prop => ({
                    prop,
                    key: PADDING_SIDE_KEYS[prop],
                    label: prop.replace('pad_', ''),
                }));
        },

        resolved() {
            // refreshTick is read purely to register as a render dependency.
            void this.refreshTick;
            return this.node.style(this.partName, this.state) ?? {};
        },

        effective() {
            return this.editorState.effective;
        },

        uniform() {
            return this.editorState.enabled;
        },

        editorState() {
            return paddingEditorState(this.resolved, { overallScale: this.overallScale });
        },

        overallEnabled() {
            return this.uniform;
        },

        overallValue() {
            return this.overallEnabled ? this.effective.top : undefined;
        },

        overallModified() {
            return this.isExplicit('padding') || (this.allAsSides && this.sides.some(side => this.isExplicit(side.prop)));
        },
    },

    methods: {
        // Every write below goes through one transaction — see the module
        // comment: Overall clears four overrides, and a side edit may normalize
        // four of them away, so both are multi-write edits.
        transact(apply) {
            runPropertyTransaction(this.node, apply, {
                description: 'Change padding',
                pageUuid: getObjectPage(this.node)?.id(),
            });
        },

        onOverall(value) {
            if (value === '') return;
            this.transact(() => applyOverallPadding(this.node, this.partName, this.state, value, { writeSides: this.allAsSides }));
        },

        toggleExpanded() {
            this.expanded = !this.expanded;
        },

        onSide(prop, value) {
            if (value === '') return;
            this.transact(() => applySidePadding(this.node, this.partName, this.state, prop, value, {
                overallScale: this.overallScale,
                writeSides: this.allAsSides,
            }));
        },

        isExplicit(prop) {
            return this.node.hasExplicitStyle(this.partName, prop, this.state);
        },

        resetSide(prop) {
            if (prop === 'padding' && this.allAsSides) return this.resetOverall();
            this.transact(() => this.node.resetStateStyle(this.partName, this.state, prop));
        },

        resetOverall() {
            this.transact(() => {
                if (this.allAsSides) {
                    resetOverallPadding(this.node, this.partName, this.state);
                } else {
                    this.node.resetStateStyle(this.partName, this.state, 'padding');
                }
            });
        },

        inputMin(prop) {
            return this.node.getStylePropertyDefinition?.(this.partName, prop)?.min;
        },

        inputMax(prop) {
            return this.node.getStylePropertyDefinition?.(this.partName, prop)?.max;
        },
    },
};
</script>
